import loadable from '@loadable/component';
import React from 'react';
import { connect } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { OriginArea } from '@videoblocks/events-ts/lib/storyblocks/search/OriginAreaEnum';
import { Telemetry } from '@videoblocks/kafka-rest-client';

import {
  selectIsPolling,
  openVideoEditorModal,
  selectAIEditButtonVisible,
} from '../../app/ContentEdit/ContentEditSlice';
import { meetsAiEditFrameRateRequirement } from '../../app/ContentEdit/utils/aiEditFrameRate';
import { openAiVideoEditor } from '../../app/ContentEdit/utils/aiVideoEditUrl';
import { ContentClass, ContentType } from '../../app/Shared/enums';
import {
  selectAiToolkitVideoEditEnabled,
  selectHasAudioSubscription,
  selectHasImagesSubscription,
  selectHasSubscriptionForContentClass,
} from '../../auth/AuthSelectors';
import { contentTypes } from '../../common/Constants';
import { withUseMedia } from '../../common/hooks/useMedia';
import { ucfirst } from '../../common/utils';
import AssetId, { getLabel } from '../components/AssetId';
import BrowseOrientedStockItemCategoryLinks from '../components/BrowseOrientedCategoryLinks/BrowseOrientedStockItemCategoryLinks';
import { JustifiedGallerySimilarStockItems } from '../components/JustifiedGallerySimilarStockItems';
import MusicRecommendationList from '../components/MusicRecommendationList';
import StockItemContributor from '../components/StockItemContributor';
import StockItemInfo from '../components/StockItemInfo';
import { PilledStockItemKeywordLinks } from '../components/StockItemKeyWordLinks';
import StockItemTitle from '../components/StockItemTitle';
import VideoStockItemPreview from '../components/VideoStockItemPreview';
import DetailsAIEditButton from '../components/buttons/DetailsAIEditButton';
import ReportErrorButton from '../components/buttons/ReportErrorButton';
import FormatSelector from '../components/download/FormatSelector';
import MemberDetailsDownloadInfo from '../components/download/cta/MemberDetailsDownloadInfo';
import MemberDownloadUpgradeCTA from '../components/download/cta/MemberDownloadUpgradeCTA';
import VisitorSignUpButton from '../components/download/cta/VisitorSignUpButton';
import {
  selectMusicRecommendations,
  selectShowSubscriptionValueProps,
  selectUseSameShootSameModelDetails,
} from '../selectors/detailsSelectors';
import AbstractItemDetailsContainer, {
  AbstractItemDetailsContainerProps,
  commonStateToProps,
} from './AbstractItemDetailsContainer';

import './stock-item-details.less';

const TabbedRelatedVideo = loadable(
  () =>
    import(
      /* webpackChunkName: "TabbedRelatedVideo" */ '../../common/components/TabbedRelatedVideo'
    )
);

interface Props extends AbstractItemDetailsContainerProps {
  isMobile: boolean;
  mediaQuery: boolean;
  useSameShootSameModelDetails: boolean;
  allowedToSeeAIEditButton: boolean;
  isContentEditPolling: boolean;
  aiToolkitVideoEditEnabled: boolean;
  showSubscriptionValueProps: boolean;
  dispatch: any;
}

class VideoDetailsContainer extends AbstractItemDetailsContainer<Props> {
  stockItemDetails() {
    const {
      stockItem: {
        previewUrl,
        shouldStockItemHaveNewFlag,
        title,
        id,
        assetId,
        contentType,
      },
      mediaQuery,
      hasSubscription,
      stockItemFormats,
      useSameShootSameModelDetails,
    } = this.props;

    const contentTypesString = 'HD footage, 4k clips, and templates';
    const isVertical = this.props?.stockItem?.isVertical;

    return (
      <>
        <div className="md:flex md:flex-column bg-gray-900 rounded-2xl stockItemDetails-row">
          <div className="xl:mr-2 flex flex-grow-2 relative flex-column bg-gray-950 rounded-l-2xl rounded-tr-2xl xl:w-3/4 xl:max-w-3/4">
            <button
              className={twMerge(
                'text-white absolute right-2 mt-4 mr-2 px-4 py-2 font-semibold text-sm border-0 cursor-pointer z-10 transition-all bg-gray-500 hover:bg-gray-600 bg-opacity-75 hover:bg-opacity-50 rounded-lg'
              )}
              onClick={() => {
                // scroll to bottom of video, so <SimilarClipsHeader /> is just visible below the top nav
                // todo: figure out why element.scrollTo({ top: toolbarHeight }) doesn't work
                const element =
                  document.getElementById('stock-item-info').parentElement;
                window.scrollTo({
                  top: element?.offsetHeight,
                  behavior: 'smooth',
                });
              }}
            >
              {useSameShootSameModelDetails ? 'Related Video' : 'Similar Clips'}
            </button>
            <VideoStockItemPreview
              className="flex rounded-t-2xl"
              previewUrl={previewUrl}
              shouldStockItemHaveNewFlag={shouldStockItemHaveNewFlag}
              title={title}
              resolution={stockItemFormats[0]?.resolution}
              isVertical={isVertical}
            />
            {!mediaQuery && <StockItemInfo />}
          </div>
          <div className="flex flex-column md:mx-4 xl:w-1/4 min-w-1/4 text-white">
            <div className="relative flex flex-col flex-1 mx-4 md:mx-0 justify-center text-white">
              {!mediaQuery && (
                <div className="text-lg text-center">
                  {this.getDownloadsRemainingMessaging()}
                </div>
              )}
              <MemberDetailsDownloadInfo
                needsToUpgrade={
                  hasSubscription && this.upgradeRequiredForMember()
                }
                contentTypesString={contentTypesString}
                className="text-white"
                isVideoDetailsPage={true}
              />
              <StockItemTitle />
              <StockItemContributor />
              <AssetId assetId={assetId} label={getLabel(contentType)} />
              <FormatSelector />
              {this.buildDownloadCta(contentTypesString, ContentClass.Video)}
              {this.props.allowedToSeeAIEditButton &&
                contentType === contentTypes.FOOTAGE &&
                meetsAiEditFrameRateRequirement(stockItemFormats) && (
                  <DetailsAIEditButton
                    onClick={this.handleAIEditAction}
                    disabled={this.props.isContentEditPolling}
                  />
                )}
            </div>
            {mediaQuery && <StockItemInfo />}
            {!mediaQuery && (
              <div className="relative mb-4 mr-4 text-right mt-4 lg:mt-0">
                <ReportErrorButton stockItemId={id} />
              </div>
            )}
          </div>
        </div>
        {mediaQuery && (
          <div className="mt-4 text-center">
            <ReportErrorButton stockItemId={id} color="black" />
          </div>
        )}
      </>
    );
  }

  buildNonSubscriberDownloadCta(contentClass: ContentClass) {
    const { hasOtherSubscriptions, isLoggedIn, showSubscriptionValueProps } =
      this.props;

    const cta = hasOtherSubscriptions
      ? `Add ${ucfirst(contentClass)} Plan to Download this Content`
      : `${isLoggedIn ? 'Subscribe' : 'Sign Up'} to Download`;

    // RFPC-1336: in the messaging_updates `test` variant (visitors only), show
    // the value-props box in place of the plain CTA.
    return (
      <VisitorSignUpButton
        makerEditorLink={this.generateMakerLink()}
        signUpButtonCta={cta}
        contentClass={ContentClass.Video}
        showValueProps={showSubscriptionValueProps}
        onSignUpClick={this.buildPaywallBlockedDownloadHandler()}
        showMakerHook={false}
        showLicenseLinks={false}
        showFavoriteButton={false}
      />
    );
  }

  buildMemberDownloadCta() {
    return (
      <MemberDownloadUpgradeCTA
        primaryActionHandler={this.buildMemberDownloadHandler()}
      />
    );
  }

  render() {
    let title = 'Keywords';

    const {
      stockItem: {
        contentType,
        contentClass,
        categories,
        keywords,
        detailsUrl,
      },
      mvtPages,
      musicRecommendations,
    } = this.props;

    if (contentType === contentTypes.FOOTAGE) {
      title = `Related Stock Video ${title}`;
    } else if (contentType === contentTypes.TEMPLATE) {
      title = `Related Template ${title}`;
    } else if (contentType === contentTypes.MOTION_BG) {
      title = `Related Background ${title}`;
    }

    const browseSimilarRows = [
      () => (
        <BrowseOrientedStockItemCategoryLinks
          categories={categories}
          contentType={contentType}
          mvtPages={mvtPages}
          linkClassName="flex-wrap gap-y-3 gap-x-2"
          pillButtons={true}
        />
      ),
    ];

    browseSimilarRows.unshift(() => (
      <PilledStockItemKeywordLinks keywords={keywords} title={title} />
    ));
    const useSameShootSameModelDetails =
      this.props.useSameShootSameModelDetails;

    return (
      <div id="details-app-container">
        <div className="stockItemDetails">
          <div className="container-fluid px-4">
            {this.stockItemDetails()}
            <div
              className={twMerge(
                'flex flex-col gap-y-6 md:grid mt-10 md:grid-cols-3 md:gap-x-6 md:gap-y-0',
                useSameShootSameModelDetails && '-mx-4 md:mx-0'
              )}
            >
              {useSameShootSameModelDetails ? (
                <div
                  className={twMerge(
                    !musicRecommendations.length
                      ? 'md:col-span-full'
                      : 'md:col-span-2'
                  )}
                >
                  <TabbedRelatedVideo
                    stockItemId={this.props.stockItem.id}
                    contentType={
                      this.props.stockItem.contentType as ContentType
                    }
                    selectedTabButtonClassNames="bg-gray-900 hover:bg-gray-1000"
                    tabListClassNames="px-4 md:px-0"
                    pageSize={12}
                    collapseAllOnlyAfterViewMore
                    onCollapse={() => {
                      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                    }}
                  />
                </div>
              ) : (
                <div
                  className={twMerge(
                    'stockItemDetails-similarClipsRow mx-2 md:col-start-1 md:row-start-1 md:row-end-2',
                    !musicRecommendations.length
                      ? 'md:col-end-4'
                      : 'md:col-end-3'
                  )}
                >
                  <div className="stockItemDetails-section">
                    <JustifiedGallerySimilarStockItems
                      headerClassName={twMerge(
                        'mt-0',
                        !musicRecommendations.length && 'leading-6'
                      )}
                      originArea={OriginArea.SIMILAR_CLIPS}
                      detailsUrl={detailsUrl}
                      contentClass={contentClass}
                      contentType={contentType}
                    />
                  </div>
                </div>
              )}

              <MusicRecommendationList
                className={`md:col-start-3 md:row-start-1 md:row-end-5 gap-y-4 md:flex-1 px-4 md:px-0 ${
                  this.props.useSameShootSameModelDetails ? 'md:gap-y-5' : ''
                }`}
                stockItems={musicRecommendations}
              />
              {browseSimilarRows.map((Row, idx) => (
                <div
                  className={twMerge(
                    `stockItemDetails-row md:col-start-1 md:row-start-${
                      idx + 2
                    } md:row-end-${idx + 3}`,
                    musicRecommendations.length
                      ? 'md:col-end-3'
                      : 'md:col-end-4'
                  )}
                  key={idx}
                >
                  <div
                    className={twMerge(
                      'stockItemDetails-section',
                      this.props.useSameShootSameModelDetails && 'md:mx-0'
                    )}
                  >
                    <Row />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  handleAIEditAction = () => {
    if (
      this.props.allowedToSeeAIEditButton &&
      this.props.stockItem.contentType === contentTypes.FOOTAGE &&
      // AI edit cannot handle sources above 30fps.
      meetsAiEditFrameRateRequirement(this.props.stockItemFormats)
      // TODO: add a prop to the component to check if it's a vertical video
    ) {
      if (this.props.aiToolkitVideoEditEnabled) {
        openAiVideoEditor(this.props.stockItem);
      } else {
        this.props.dispatch(openVideoEditorModal(this.props.stockItem));
      }

      Telemetry.increment('ai.contentEdit.clicked.stockItemDetails');
    }
  };

  upgradeRequiredForMember() {
    const { stockItem, downloadPermissions, selectedAcquisitionOption } =
      this.props;

    if (stockItem.isTemplate && !downloadPermissions.aeTemplates) {
      return true;
    }

    return (
      selectedAcquisitionOption.quality === '4K' && !downloadPermissions.fourK
    );
  }
}

function mapStateToProps(state) {
  const hasSubscription = selectHasSubscriptionForContentClass(
    ContentClass.Video
  )(state);

  return {
    ...commonStateToProps(state),
    hasSubscription,
    hasOtherSubscriptions: Boolean(
      selectHasAudioSubscription(state) || selectHasImagesSubscription(state)
    ),
    siteSubscription: hasSubscription,
    musicRecommendations: selectMusicRecommendations(state),
    useSameShootSameModelDetails: selectUseSameShootSameModelDetails(state),
    allowedToSeeAIEditButton: selectAIEditButtonVisible(state),
    isContentEditPolling: selectIsPolling(state),
    aiToolkitVideoEditEnabled: selectAiToolkitVideoEditEnabled(state),
    showSubscriptionValueProps: Boolean(
      selectShowSubscriptionValueProps(state)
    ),
  };
}

// @ts-expect-error This is complicated. There is documentation at https://react-redux.js.org/using-react-redux/usage-with-typescript#typing-the-connect-higher-order-component
const connectedComponent = connect(mapStateToProps)(VideoDetailsContainer);

export default withUseMedia<Props>(connectedComponent, {
  query: '(max-width: 1200px)', // true when width < 1200px (i.e. isMobile)
  defaultState: false,
});
